import { useMemo } from "react";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import {
  useActiveCheckInConversation,
  useOpenConversation,
} from "../../../common";
import { LargeCustomIcon } from "../../layer00";
import { BoZoneCallout, StyledCard, BoZoneDivider } from "../../layer01";
import { BoZoneSuggestionCard } from "../../layer06";
import thinkingRobot from "../../../../assets/images/thinkingRobot.png";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetRollingCheckInSummaryQuery } from "app/containers/TeamsTab/teamsAiServiceApi";
import { useGetCheckInSummaryQuery } from "app/containers/TeamsTab/teamsCheckInApi";

type BoZoneNoticedCardProps = {
  userAccountId: number | null;
};

const BodySkeleton = () => (
  <Stack direction="column" rowGap=".25rem">
    <Skeleton width="95%" height={16} />
    <Skeleton width="85%" height={16} />
    <Skeleton width="60%" height={16} />
  </Stack>
);

const SuggestionCardSkeleton = () => (
  <BoZoneCallout>
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "flex-start", sm: "center" }}
      justifyContent="space-between"
      gap="1.5rem"
    >
      <Stack direction="column" rowGap=".75rem" flex={1} width="100%">
        <Skeleton width="40%" height={22} />
        <BodySkeleton />
      </Stack>
      <Skeleton
        variant="rounded"
        width="7.5rem"
        height="2.25rem"
        sx={{ flexShrink: 0 }}
      />
    </Stack>
  </BoZoneCallout>
);

const CHECK_IN_NUDGE_MESSAGES = [
  "A quick check-in helps me understand how your week is really going. It only takes a few minutes, and the more you share, the better I can tailor my suggestions to you.",
  "I’d love to hear how things are going! Your check-in helps me spot what’s working, what’s slowing you down, and where I can lend a hand. Ready when you are.",
  "Before I can offer helpful ideas, I need to hear from you. Take a few minutes to check in and tell me what’s on your plate. I’ll use it to find ways to make your week easier.",
  "Every check-in helps me get to know you a little better. Share how you’re feeling about your work this week, and I’ll turn it into ideas that actually fit how you work.",
  "Got a few minutes? Let’s catch up on your week. Your check-in gives me the context I need to celebrate your wins, spot roadblocks early, and suggest next steps that help.",
  "Your check-in is where it all starts. Tell me about your wins, challenges, and what’s coming up next, and I’ll help you find small changes that make a big difference.",
  "I’m here to make your work life a little easier, but I need your input first. A quick check-in lets me learn what matters most to you right now so I can help where it counts.",
  "No pressure and no wrong answers, just a quick chat about how your work is going. What you share helps me support you and keep you moving toward your goals.",
];

export const BoZoneNoticedCard = ({
  userAccountId,
}: BoZoneNoticedCardProps) => {
  const { data: rollingData, isFetching: isRollingFetching } =
    useGetRollingCheckInSummaryQuery(
      userAccountId !== null
        ? {
            userAccountId: userAccountId,
            mostRecentCompletedCheckIn: true,
          }
        : skipToken
    );

  const rollingSummary =
    rollingData && "success" in rollingData && rollingData.success
      ? rollingData.data.overallSummary
      : null;

  // Picked once per mount so the message changes each time the user lands on the page.
  const checkInNudgeMessage = useMemo(
    () =>
      CHECK_IN_NUDGE_MESSAGES[
        Math.floor(Math.random() * CHECK_IN_NUDGE_MESSAGES.length) // NOSONAR: non-security use, rotating nudge copy
      ],
    []
  );

  // Same args as BoZoneActivityDetailSection so RTK Query shares the cached
  // request. hasPendingCheckIn is evaluated as of now regardless of the params.
  const { data: checkInSummaryData, isFetching: isCheckInSummaryFetching } =
    useGetCheckInSummaryQuery(
      userAccountId !== null
        ? {
            userAccountId: userAccountId,
            mostRecentCompletedCheckIn: true,
          }
        : skipToken
    );

  const openConversation = useOpenConversation();

  const pendingCheckInConversation =
    useActiveCheckInConversation(userAccountId);

  const showNoticed = isRollingFetching || !!rollingSummary;

  if (!showNoticed) {
    return null;
  }

  const getCheckInCard = () => {
    if (isCheckInSummaryFetching) {
      return <SuggestionCardSkeleton />;
    }
    if (!checkInSummaryData?.hasPendingCheckIn) {
      return null;
    }

    return (
      <>
        <BoZoneDivider />
        <BoZoneSuggestionCard
          title={"Let's first check in"}
          body={checkInNudgeMessage}
          actionLabel={"Check in"}
          actionIcon={<LargeCustomIcon icon="SPARKLE" stroke="#FFFFFF" />}
          onAction={
            pendingCheckInConversation
              ? () => openConversation(pendingCheckInConversation)
              : undefined
          }
          isActionDisabled={!pendingCheckInConversation}
        />
      </>
    );
  };

  return (
    <StyledCard title="Things I Noticed About You (Over the Last 4 Check Ins)">
      <Stack direction="column" rowGap="1rem">
        <BoZoneCallout>
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            alignItems={{
              xs: "flex-start",
              sm: "center",
            }}
            columnGap="1.5rem"
            rowGap="1rem"
          >
            <Box
              component="img"
              src={thinkingRobot}
              alignSelf={{
                xs: "center",
                sm: "flex-start",
              }}
              sx={{
                width: "6.625rem",
                height: "5.4375rem",
                aspectRatio: 106 / 87,
                mixBlendMode: "darken",
              }}
            />
            <Stack
              direction="column"
              rowGap=".5rem"
              flex={{
                xs: "0 0 auto",
                sm: "1 1 0",
              }}
              width={{
                xs: "100%",
                sm: "auto",
              }}
              minWidth={0}
            >
              <Typography variant="cardSectionTitle">
                I&rsquo;ve noticed a few things and think I can help. Got time
                for a chat?
              </Typography>
              {isRollingFetching ? (
                <BodySkeleton />
              ) : (
                <Typography
                  variant="cardBodyRegular"
                  fontSize=".8125rem"
                  color="#202D63"
                >
                  {rollingSummary}
                </Typography>
              )}
            </Stack>
          </Stack>
        </BoZoneCallout>
        {getCheckInCard()}
      </Stack>
    </StyledCard>
  );
};
